import React, { useState } from 'react'

import { useQuery } from '@tanstack/react-query'

import { goodfellasApi } from '../../../apis'
import { useCompetitionStore } from '../../../functions/store'
import { CompetitionMatch } from '../../../types/competition'
import AddCompetition, { MatchAdminContext } from './AddCompetition'
import CompetitionCard from './CompetitionCard'
import CompetitionStats from './CompetitionStats'
import DeleteMatchModal from './DeleteMatchModal'

interface CompetitionHistoryProps {
  fighterUuid?: string
  phoneNumber?: string
  showActions?: boolean
  onAddClick?: () => void
  /** Repassado ao editor quando um admin edita a luta de outro atleta (#1292). */
  adminContext?: MatchAdminContext
}

export default function CompetitionHistory({
  fighterUuid,
  phoneNumber,
  showActions = false,
  onAddClick,
  adminContext,
}: CompetitionHistoryProps) {
  const { matches, stats, competitionActions } = useCompetitionStore()
  const [editingMatch, setEditingMatch] = useState<CompetitionMatch | null>(
    null,
  )
  const [deleteTarget, setDeleteTarget] = useState<CompetitionMatch | null>(
    null,
  )
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Fetch matches with useQuery
  const {
    isLoading,
    isFetching,
    error: queryError,
  } = useQuery({
    queryKey: ['competitionMatches', fighterUuid, phoneNumber],
    queryFn: async () => {
      const endpoint = fighterUuid
        ? `/competition/matches/profile/${encodeURIComponent(fighterUuid)}`
        : phoneNumber
        ? `/competition/matches/phone/${encodeURIComponent(phoneNumber)}`
        : `/competition/matches`

      const response = await goodfellasApi.get(endpoint)

      if (response.data.success) {
        competitionActions.setMatches(response.data.data)
        return response.data.data
      } else {
        throw new Error('Erro ao carregar histórico')
      }
    },
    enabled: !!fighterUuid || !!phoneNumber,
    staleTime: 0,
  })

  const loading = isLoading || isFetching
  const error = queryError?.message || null

  const handleEdit = (match: CompetitionMatch) => {
    setEditingMatch(match)
  }

  const handleDeleteClick = (matchId: string) => {
    const target = matches.find((m) => m.identificator.uuid === matchId) || null
    setDeleteError(null)
    setDeleteTarget(target)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    setDeleteLoading(true)
    setDeleteError(null)
    try {
      const response = await goodfellasApi.delete(
        `/competition/matches/${deleteTarget.identificator.uuid}`,
        { validateStatus: () => true },
      )

      if (
        response.status >= 200 &&
        response.status < 300 &&
        response.data?.success
      ) {
        competitionActions.removeMatch(deleteTarget.identificator.uuid)
        setDeleteTarget(null)
      } else {
        setDeleteError(
          response.data?.error || 'Erro ao excluir luta. Tente novamente.',
        )
      }
    } catch (err) {
      console.error('Error deleting match:', err)
      setDeleteError('Erro ao excluir luta. Tente novamente.')
    } finally {
      setDeleteLoading(false)
    }
  }

  if (loading && matches.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent mb-4"></div>
        <p className="text-gray-400">Carregando competições...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-16 bg-red-500/10 rounded-2xl border border-red-500/20">
        <div className="text-red-400 mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-16 w-16 mx-auto"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <p className="text-red-400 text-lg font-medium mb-2">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="text-sm text-gray-400 hover:text-white underline transition-colors"
        >
          Tentar novamente
        </button>
      </div>
    )
  }

  // Empty State
  if (matches.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="mb-6">
          <div className="w-24 h-24 mx-auto bg-gray-800/50 rounded-full flex items-center justify-center mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-12 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
              />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-white mb-2">
            Nenhuma luta registrada
          </h3>
          <p className="text-gray-400 mb-6 max-w-md mx-auto">
            Comece a construir seu histórico competitivo registrando suas lutas
            em campeonatos e eventos.
          </p>
          {showActions && onAddClick && (
            <button
              onClick={onAddClick}
              className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl transition-all duration-200 shadow-lg hover:shadow-red-500/25 font-medium"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
              Registrar Primeira Luta
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      <DeleteMatchModal
        isOpen={Boolean(deleteTarget)}
        match={deleteTarget}
        loading={deleteLoading}
        error={deleteError}
        onClose={() => {
          if (deleteLoading) return
          setDeleteTarget(null)
        }}
        onConfirm={handleDeleteConfirm}
      />

      {editingMatch && (
        <AddCompetition
          mode="edit"
          initialMatch={editingMatch}
          adminContext={adminContext}
          onClose={() => setEditingMatch(null)}
          onSuccess={() => setEditingMatch(null)}
        />
      )}

      {/* Hero Stats Section */}
      <CompetitionStats
        stats={stats}
        onAddClick={onAddClick}
        showAddButton={showActions}
      />

      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-6 sm:mb-8">
        <h3 className="text-lg sm:text-xl font-black text-white tracking-tight">
          Histórico de Lutas
        </h3>
        <span className="text-xs sm:text-sm text-slate-400 font-semibold bg-slate-800/50 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full border border-white/10 self-start sm:self-auto">
          {matches.length} {matches.length === 1 ? 'luta' : 'lutas'}
        </span>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        {matches.map((match) => (
          <CompetitionCard
            key={match.identificator.uuid}
            match={match}
            showActions={showActions}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
          />
        ))}
      </div>
    </div>
  )
}
